import {createGroupFilterVar, useDomainIDParam} from "@/types";
import {HasPermission, Link} from "../widgets";
import {CallGroupType, GroupFilterType} from "@/api";
import {GenericTable} from "@dvrt/graphql-widgets";
import {useConcertoGenericQuery, useDomainNameQuery} from "@/hooks";
import {
    CallGroupListFragment,
    CallGroupListFragmentDoc,
    CallQueueListFragment,
    CallQueueListFragmentDoc,
} from "./queries.generated";
import {default as CreateCallGroupButton} from "./create/CreateButton";
import {default as CreateCallQueueButton} from "./call-queue/CreateButton";
import {GroupTypeDesc, groupTypesDesc} from "./desc";
import {Box} from "@mui/material";
import {defineMessages} from "react-intl";
import {commonTranslations} from "@/utils/translations";
import TableShortcutButton from "../TableShortcutButton";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {ContextFilters} from "@/context/AuthProvider";

type CallGroupListProps = {
    domainId: number;
    type: GroupFilterType;
};

const CreateButton = ({desc, domainId, onSuccess}: {desc: GroupTypeDesc; domainId: number; onSuccess: () => void}) => {
    const {domainName} = useDomainNameQuery(domainId);
    const contextFilter = ContextFilters.domain(domainName);

    if (desc.types.length === 1 && desc.types[0] === CallGroupType.CallQueue) {
        return (
            <HasPermission name="add_call_group" contextFilter={contextFilter}>
                <CreateCallQueueButton
                    domainId={domainId}
                    onSuccess={onSuccess}
                    title={desc.createTitle}
                    successItemPath={desc.itemPath}
                />
            </HasPermission>
        );
    }

    return (
        <HasPermission name="add_call_group" contextFilter={contextFilter}>
            <CreateCallGroupButton
                domainId={domainId}
                onSuccess={onSuccess}
                title={desc.createTitle}
                successItemPath={desc.itemPath}
            />
        </HasPermission>
    );
};

const CallGroupListBase = ({domainId, type}: CallGroupListProps) => {
    const desc = groupTypesDesc[type];

    const {domainName} = useDomainNameQuery(domainId);

    const query = useConcertoGenericQuery<CallGroupListFragment | CallQueueListFragment>(
        "groups",
        type === GroupFilterType.CallQueue ? CallQueueListFragmentDoc : CallGroupListFragmentDoc,
        {
            filtering: {
                argumentName: "filter",
                placeHolder: commonTranslations.filterPlaceholders.nameOrE164OrShortNumber,
                idPrefix: "search-call-groups",
            },
            fetchPolicy: "cache-and-network",
            extraVariables: {
                domainId,
                type: createGroupFilterVar(type),
            },
            initialOrdering: ["name"],
        },
    );

    return (
        <Box sx={{paddingX: 3}}>
            <GenericTable<CallGroupListFragment | CallQueueListFragment>
                query={query}
                labels={defineMessages({
                    id: {id: "unused", defaultMessage: "Unused"},
                    name: {id: "name", defaultMessage: "Name"},
                    number: {id: "unused", defaultMessage: "Unused"},
                    "number.e164": {id: "e164", defaultMessage: "E.164"},
                    "number.shortNumber": {id: "short_number", defaultMessage: "Short number"},
                })}
                hideColumns={["id", "number"]}
                createButton={<CreateButton desc={desc} domainId={domainId} onSuccess={query.refetch} />}
                dataRenderers={{
                    name: (value: string, row: CallGroupListFragment | CallQueueListFragment) => {
                        const page = "settings";
                        return <Link to={`/domains/${domainId}/${desc.itemPath}/${row.id}/${page}`}>{value}</Link>;
                    },
                }}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => (
                            <HasPermission name="delete_call_group" contextFilter={ContextFilters.domain(domainName)}>
                                <TableShortcutButton
                                    shortcutPath={`${row.id}/delete`}
                                    tooltipTitle={commonTranslations.delete}
                                    buttonIcon={faTrash}
                                    color="error"
                                />
                            </HasPermission>
                        ),
                    },
                ]}
                orderableFields="all"
            />
        </Box>
    );
};

export const CallGroupList = () => {
    const domainId = useDomainIDParam();

    return <CallGroupListBase domainId={domainId} type={GroupFilterType.CallGroup} />;
};

export const CallQueueList = () => {
    const domainId = useDomainIDParam();

    return <CallGroupListBase domainId={domainId} type={GroupFilterType.CallQueue} />;
};
